import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from './utils/api';
import { validateText } from './utils/inputValidation';
import { formatKrw } from './utils/currency';
import './PromotionManager.css';

const PER_PAGE = 20;

// Field caps mirror backend/app/api/promotions.py → CouponCreate. They are the
// server's numbers, not new limits invented here.
const CAP = {
  code: 64,
  code_prefix: 24,
  campaign_code: 64,
  product_code: 40,
  description: 500,
  reason: 500,
};

// Server-derived status (promotions.py `_STATUS_CASE`). Colours follow the
// STATUS_COLORS/StatusBadge pattern in ManualPaymentManager.jsx.
const STATUS_COLORS = {
  active: '#16a34a',
  expired: '#6b7280',
  revoked: '#dc2626',
  exhausted: '#d97706',
};

const REDEMPTION_COLORS = {
  reserved: '#d97706',   // in flight — will settle at the discounted price
  redeemed: '#16a34a',
  released: '#6b7280',
};

function StatusBadge({ status }) {
  return (
    <span className="prm-badge" style={{ background: STATUS_COLORS[status] || '#6b7280' }}>
      {status || 'unknown'}
    </span>
  );
}

function StateBadge({ state }) {
  return (
    <span className="prm-badge" style={{ background: REDEMPTION_COLORS[state] || '#6b7280' }}>
      {state || 'unknown'}
    </span>
  );
}

function formatDate(d) {
  if (!d) return '—';
  return String(d).slice(0, 10);
}

function formatDateTime(d) {
  if (!d) return '—';
  const parsed = new Date(d);
  return Number.isNaN(parsed.getTime()) ? String(d).slice(0, 16) : parsed.toLocaleString();
}

function discountLabel(type, amountOff, percentOff) {
  if (type === 'percent') return percentOff != null ? `${percentOff}%` : '—';
  if (type === 'fixed') return amountOff != null ? `${formatKrw(amountOff)} off` : '—';
  return '—';
}

// FastAPI hands back `detail` as a string for HTTPException but as an ARRAY of
// objects for a 422 body-validation failure. Rendering that array straight into
// JSX throws ("Objects are not valid as a React child"), so flatten it here.
function errorText(payload, fallback) {
  const d = payload && payload.detail;
  if (typeof d === 'string' && d) return d;
  if (Array.isArray(d)) {
    const msgs = d.map(x => (typeof x === 'string' ? x : x && x.msg)).filter(Boolean);
    if (msgs.length) return msgs.join('; ');
  }
  return fallback;
}

// Applied-vs-converted is the whole point of this screen, so the rate gets a
// value AND a bar. A zero rate off zero applications is not a bad rate — it is
// no data — and must not be painted red.
function RateCell({ rate, applied }) {
  if (!applied) return <span className="prm-rate-empty">—</span>;
  const v = Number(rate || 0);
  const tone = v >= 50 ? '#16a34a' : v >= 20 ? '#d97706' : '#dc2626';
  return (
    <div className="prm-rate">
      <span className="prm-rate-value" style={{ color: tone }}>{v}%</span>
      <span className="prm-rate-track">
        <span
          className="prm-rate-fill"
          style={{ width: `${Math.min(100, Math.max(0, v))}%`, background: tone }}
        />
      </span>
    </div>
  );
}

// ── Create modal ─────────────────────────────────────────────────────────────

// The discount-type selector deliberately starts EMPTY and the submit button
// stays disabled until it is set. A field that silently defaulted to "percent"
// turns an admin typing 50 (meaning ₩50,000) into half off every sale, and the
// first sign of it is the settlement report. `amount_off_krw` and `percent_off`
// are also kept in SEPARATE state so a value typed under one type can never
// carry over into the other.
function CreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    code: '',
    code_prefix: '',
    campaign_code: '',
    description: '',
    discount_type: '',
    amount_off_krw: '',
    percent_off: '',
    product_code: '',
    max_redemptions: '',
    expires_on: '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);   // { id, code, expires_at, preview }
  const [copied, setCopied] = useState(false);

  // The catalogue's plan codes, fetched rather than hardcoded.
  //
  // `product_code` must match a catalogue code EXACTLY — coupon_service.resolve
  // does `product_code = :pc`, case-sensitive — and NOTHING validates it at
  // creation. A typo therefore creates a coupon that silently never matches,
  // and the first sign of it is a customer saying the code did not work.
  //
  // Fetched, not hardcoded, because a client-side price table is exactly how
  // /pricing came to advertise a ₩10,000 Pro plan against a real ₩499,000 one.
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/v1/admin/promotions/plans');
        if (!res.ok) return;
        const d = await res.json();
        if (!cancelled) setPlans(d.plans || []);
      } catch { /* the list is an aid, not a gate — the form still works */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const amount = Number(form.amount_off_krw);
  const percent = Number(form.percent_off);
  // Both values are integers server-side (KRW has no minor units; percent_off is
  // an int 1..99), so a decimal is rejected here rather than as a 422.
  const valueOk =
    form.discount_type === 'fixed'
      ? form.amount_off_krw !== '' && Number.isInteger(amount) && amount > 0
      : form.discount_type === 'percent'
        ? form.percent_off !== '' && Number.isInteger(percent) && percent >= 1 && percent <= 99
        : false;

  // No discount type picked → nothing to submit.
  const canSubmit = !!form.discount_type && valueOk && !saving;

  const handleSubmit = async () => {
    setError('');

    // Identifier-charset checks up front so a typo reads as a sentence instead
    // of a 422 body-validation dump.
    for (const [key, label] of [
      ['code', 'Code'],
      ['code_prefix', 'Code prefix'],
      ['campaign_code', 'Campaign code'],
      ['product_code', 'Product code'],
    ]) {
      const val = form[key].trim();
      if (!val) continue;
      const check = validateText(val, 'identifier');
      if (!check.ok) {
        setError(`${label}: letters, numbers, hyphen and underscore only.`);
        return;
      }
    }
    if (form.max_redemptions !== ''
        && !(Number.isInteger(Number(form.max_redemptions)) && Number(form.max_redemptions) > 0)) {
      setError('Max redemptions must be a whole number greater than 0, or left blank for unlimited.');
      return;
    }

    const body = {
      discount_type: form.discount_type,
      code: form.code.trim() || null,
      code_prefix: form.code.trim() ? null : (form.code_prefix.trim() || null),
      campaign_code: form.campaign_code.trim() || null,
      description: form.description.trim() || null,
      amount_off_krw: form.discount_type === 'fixed' ? amount : null,
      percent_off: form.discount_type === 'percent' ? percent : null,
      product_code: form.product_code.trim() || null,
      max_redemptions: form.max_redemptions !== '' ? Number(form.max_redemptions) : null,
      expires_on: form.expires_on || null,
    };

    setSaving(true);
    try {
      const res = await apiFetch('/api/v1/admin/promotions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setCreated(data);
      } else if (res.status === 409) {
        setError(errorText(data, 'That code already exists.'));
      } else {
        setError(errorText(data, 'Could not create the coupon.'));
      }
    } catch {
      setError('Network error while creating the coupon.');
    } finally {
      setSaving(false);
    }
  };

  const copyCode = async () => {
    if (!created?.code) return;
    try {
      await navigator.clipboard.writeText(created.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const finish = () => {
    onCreated();
    onClose();
  };

  // ── Result view: the code (it may have been server-generated) + the price
  // preview for every SKU it can touch.
  if (created) {
    const preview = Array.isArray(created.preview) ? created.preview : [];
    const blocked = preview.filter(p => !p.usable);
    return (
      <div className="prm-overlay" onClick={finish}>
        <div className="prm-modal prm-modal-wide" onClick={e => e.stopPropagation()}>
          <h3>Coupon created</h3>

          <div className="prm-created-code">
            <span className="prm-created-label">Code</span>
            <code className="prm-code prm-code-lg">{created.code}</code>
            <button type="button" className="prm-btn prm-btn-secondary prm-btn-sm" onClick={copyCode}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="prm-hint">
            Give this exact code to the customer.
            {created.expires_at
              ? ` Valid through ${formatDate(created.expires_at)} (KST).`
              : ' No expiry date.'}
          </p>

          {blocked.length > 0 && (
            <div className="prm-modal-warning">
              This coupon is refused at checkout on {blocked.length} of {preview.length} plan
              {preview.length === 1 ? '' : 's'} — see the notes below.
            </div>
          )}

          <h4 className="prm-subhead">What it does to each plan</h4>
          <div className="prm-table-wrap">
            <table className="prm-table">
              <thead>
                <tr>
                  <th>Product</th><th>List → Net</th><th>Discount</th><th>Usable</th><th>Note</th>
                </tr>
              </thead>
              <tbody>
                {preview.length === 0 && (
                  <tr><td colSpan={5} className="prm-empty">No sellable plans matched this coupon.</td></tr>
                )}
                {preview.map(p => (
                  <tr key={p.product_code} className={p.usable ? '' : 'prm-row-blocked'}>
                    <td><code className="prm-code">{p.product_code}</code></td>
                    <td>
                      <span className="prm-strike">{formatKrw(p.list_amount_krw)}</span>
                      {' → '}
                      <strong>{formatKrw(p.net_amount_krw)}</strong>
                    </td>
                    <td>−{formatKrw(p.discount_krw)}</td>
                    <td>
                      {p.usable
                        ? <span className="prm-yes">Yes</span>
                        : <span className="prm-no">No</span>}
                    </td>
                    <td className="prm-note-cell">{p.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="prm-modal-actions">
            <button className="prm-btn prm-btn-primary" onClick={finish}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="prm-overlay" onClick={onClose}>
      <div className="prm-modal prm-modal-wide" onClick={e => e.stopPropagation()}>
        <h3>Create Coupon</h3>
        {error && <div className="prm-modal-error">{error}</div>}

        {/* Discount type — NO DEFAULT. */}
        <div className="prm-type-block">
          <label className="prm-field-label">Discount type *</label>
          <div className="prm-type-choices">
            <label className={`prm-type-choice ${form.discount_type === 'fixed' ? 'prm-type-active' : ''}`}>
              <input
                type="radio"
                name="prm-discount-type"
                value="fixed"
                checked={form.discount_type === 'fixed'}
                onChange={() => set('discount_type', 'fixed')}
              />
              <span>
                <strong>Fixed amount</strong>
                <small>Take a number of won off the list price</small>
              </span>
            </label>
            <label className={`prm-type-choice ${form.discount_type === 'percent' ? 'prm-type-active' : ''}`}>
              <input
                type="radio"
                name="prm-discount-type"
                value="percent"
                checked={form.discount_type === 'percent'}
                onChange={() => set('discount_type', 'percent')}
              />
              <span>
                <strong>Percentage</strong>
                <small>Take a percentage off the list price (1–99)</small>
              </span>
            </label>
          </div>
          {!form.discount_type && (
            <p className="prm-hint prm-hint-strong">
              Pick a discount type before entering a value — the same number means
              ₩50,000 off under one and half off under the other.
            </p>
          )}
        </div>

        <div className="prm-form-grid">
          {/* Value — only the field for the chosen type is ever rendered, so a
              number typed under one type cannot be reinterpreted by the other. */}
          <div className="prm-form-group prm-span-2">
            <label>Discount value *</label>
            {form.discount_type === 'percent' ? (
              <div className="prm-value-row">
                <input
                  type="number"
                  min="1"
                  max="99"
                  step="1"
                  placeholder="1–99"
                  value={form.percent_off}
                  onChange={e => set('percent_off', e.target.value)}
                />
                <span className="prm-unit">%</span>
              </div>
            ) : (
              <div className="prm-value-row">
                <input
                  type="number"
                  min="1"
                  step="1"
                  placeholder={form.discount_type ? 'e.g. 300000' : 'Pick a discount type first'}
                  disabled={!form.discount_type}
                  value={form.amount_off_krw}
                  onChange={e => set('amount_off_krw', e.target.value)}
                />
                {/* No type picked yet → no unit is claimed either. */}
                <span className="prm-unit">{form.discount_type === 'fixed' ? 'won' : '—'}</span>
              </div>
            )}
            {form.discount_type === 'percent' && form.percent_off !== '' && !valueOk && (
              <span className="prm-field-error">Percentage must be a whole number from 1 to 99.</span>
            )}
            {form.discount_type === 'fixed' && form.amount_off_krw !== '' && !valueOk && (
              <span className="prm-field-error">Amount must be a whole number of won greater than 0.</span>
            )}
            {form.discount_type === 'fixed' && valueOk && (
              <span className="prm-field-note">{formatKrw(amount)} off</span>
            )}
          </div>

          <div className="prm-form-group">
            <label>Code</label>
            <input
              type="text"
              placeholder="Leave blank to generate one"
              maxLength={CAP.code}
              value={form.code}
              onChange={e => set('code', e.target.value)}
            />
          </div>
          <div className="prm-form-group">
            <label>Code prefix</label>
            <input
              type="text"
              placeholder="Vanity prefix for the generated code"
              maxLength={CAP.code_prefix}
              disabled={!!form.code.trim()}
              value={form.code_prefix}
              onChange={e => set('code_prefix', e.target.value)}
            />
            {!!form.code.trim() && (
              <span className="prm-field-note">Ignored — an explicit code was given.</span>
            )}
          </div>

          <div className="prm-form-group">
            <label>Campaign code</label>
            <input
              type="text"
              placeholder="Groups codes into one promotion"
              maxLength={CAP.campaign_code}
              value={form.campaign_code}
              onChange={e => set('campaign_code', e.target.value)}
            />
          </div>
          <div className="prm-form-group">
            <label>Product code</label>
            <input
              type="text"
              placeholder="Blank = any plan"
              maxLength={CAP.product_code}
              value={form.product_code}
              onChange={e => set('product_code', e.target.value)}
            />
            <span className="prm-field-note">Must match a catalogue plan code exactly.</span>
          </div>

          <div className="prm-form-group">
            <label>Max redemptions</label>
            <input
              type="number"
              min="1"
              step="1"
              placeholder="Blank = unlimited"
              value={form.max_redemptions}
              onChange={e => set('max_redemptions', e.target.value)}
            />
          </div>
          <div className="prm-form-group">
            <label>Expires on</label>
            <input
              type="date"
              value={form.expires_on}
              onChange={e => set('expires_on', e.target.value)}
            />
            <span className="prm-field-note">KST date — usable through the end of that day.</span>
          </div>

          <div className="prm-form-group prm-span-2">
            <label>Description</label>
            <textarea
              rows={2}
              maxLength={CAP.description}
              placeholder="Internal note — what this coupon is for"
              value={form.description}
              onChange={e => set('description', e.target.value)}
            />
          </div>
        </div>

        {/* Product-code reference. The field above is free text, exact-match and
            unvalidated server-side, so the admin needs the codes in front of them.
            Clicking a row fills the field — which is the actual fix for the typo
            risk; the table alone still leaves them retyping a 16-character code. */}
        {plans.length > 0 && (
          <>
            <h4 className="prm-subhead">Product codes</h4>
            <p className="prm-field-note">
              Leave <strong>Product code</strong> blank to apply the coupon to any plan,
              or click a code below to scope it to one.
            </p>
            <div className="prm-table-wrap">
              <table className="prm-table">
                <thead>
                  <tr>
                    <th>Product code</th><th>Plan</th><th>Price</th><th>Usable in a coupon?</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map(pl => (
                    <tr
                      key={pl.product_code}
                      className={pl.usable ? '' : 'prm-row-blocked'}
                      title={pl.note || 'Click to use this code'}
                    >
                      <td>
                        <button
                          type="button"
                          className="prm-code-pick"
                          onClick={() => set('product_code', pl.product_code)}
                        >
                          <code className="prm-code">{pl.product_code}</code>
                        </button>
                      </td>
                      <td>{pl.display_en}</td>
                      <td>{formatKrw(pl.price_krw)}</td>
                      <td>
                        {pl.usable
                          ? <span className="prm-yes">Yes</span>
                          : <span className="prm-no">No</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="prm-modal-warning">
          Coupons cannot be edited after creation — only revoked. Check the value
          and the unit now; the price preview on the next screen shows exactly what
          this does to every plan.
        </div>

        <div className="prm-modal-actions">
          <button className="prm-btn prm-btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="prm-btn prm-btn-primary"
            disabled={!canSubmit}
            title={!form.discount_type ? 'Pick a discount type first' : undefined}
            onClick={handleSubmit}
          >
            {saving ? 'Creating…' : 'Create Coupon'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Revoke confirm ───────────────────────────────────────────────────────────

function RevokeModal({ coupon, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    await onConfirm(coupon, reason.trim() || null);
    setBusy(false);
  };

  return (
    <div className="prm-overlay" onClick={onClose}>
      <div className="prm-modal" onClick={e => e.stopPropagation()}>
        <h3>Revoke coupon</h3>
        <div className="prm-confirm-code">
          <code className="prm-code prm-code-lg">{coupon.code}</code>
        </div>

        {/* Forward-only. See promotions.py revoke_promotion: already-reserved
            intents are deliberately NOT voided. */}
        <div className="prm-modal-warning">
          <strong>Revocation is forward-only.</strong> It stops the code being applied
          to any new purchase. It does <strong>not</strong> cancel discounted purchases
          that were already created and have not been paid yet — those still settle at
          the discounted price.
        </div>

        {coupon.reserved > 0 && (
          <p className="prm-hint prm-hint-strong">
            {coupon.reserved} checkout{coupon.reserved === 1 ? ' is' : 's are'} in flight
            on this code right now.
          </p>
        )}

        <div className="prm-form-group">
          <label>Reason (optional)</label>
          <input
            type="text"
            maxLength={CAP.reason}
            placeholder="Recorded in the audit log"
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
        </div>

        <div className="prm-modal-actions">
          <button className="prm-btn prm-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="prm-btn prm-btn-danger" disabled={busy} onClick={submit}>
            {busy ? 'Revoking…' : 'Revoke coupon'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Detail modal ─────────────────────────────────────────────────────────────

function DetailModal({ couponId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/api/v1/admin/promotions/${couponId}`);
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok) setDetail(data);
        else setError(errorText(data, 'Failed to load this coupon.'));
      } catch {
        if (!cancelled) setError('Network error loading this coupon.');
      }
    })();
    return () => { cancelled = true; };
  }, [couponId]);

  const redemptions = Array.isArray(detail?.redemptions) ? detail.redemptions : [];

  return (
    <div className="prm-overlay" onClick={onClose}>
      <div className="prm-modal prm-modal-xwide" onClick={e => e.stopPropagation()}>
        <h3>
          Coupon detail
          {detail && <> — <code className="prm-code prm-code-lg">{detail.code}</code></>}
        </h3>

        {error && <div className="prm-modal-error">{error}</div>}
        {!detail && !error && <div className="prm-loading">Loading…</div>}

        {detail && (
          <>
            <dl className="prm-detail-grid">
              <div><dt>Status</dt><dd><StatusBadge status={detail.status} /></dd></div>
              <div><dt>Campaign</dt><dd>{detail.campaign_code || '—'}</dd></div>
              <div>
                <dt>Discount</dt>
                <dd>{discountLabel(detail.discount_type, detail.amount_off_krw, detail.percent_off)}</dd>
              </div>
              <div><dt>Scope</dt><dd>{detail.product_code || 'Any plan'}</dd></div>
              <div>
                <dt>Uses</dt>
                <dd>{detail.live_redemptions ?? 0} / {detail.max_redemptions ?? '∞'}</dd>
              </div>
              <div><dt>Converted</dt><dd>{detail.converted ?? 0}</dd></div>
              <div><dt>Reserved</dt><dd>{detail.reserved ?? 0}</dd></div>
              <div><dt>Discount given</dt><dd>{formatKrw(detail.discount_given_krw)}</dd></div>
              <div><dt>Net revenue</dt><dd>{formatKrw(detail.net_revenue_krw)}</dd></div>
              <div><dt>Expires</dt><dd>{formatDate(detail.expires_at)}</dd></div>
              <div><dt>Created</dt><dd>{formatDateTime(detail.created_at)}</dd></div>
              <div><dt>Revoked</dt><dd>{formatDateTime(detail.revoked_at)}</dd></div>
            </dl>

            {detail.description && <p className="prm-detail-desc">{detail.description}</p>}
            {detail.revoked_reason && (
              <p className="prm-detail-desc">Revoked reason: {detail.revoked_reason}</p>
            )}

            <h4 className="prm-subhead">Redemptions <span className="prm-count-chip">{redemptions.length}</span></h4>
            <div className="prm-table-wrap">
              <table className="prm-table">
                <thead>
                  <tr>
                    <th>Organisation</th><th>State</th><th>Product</th>
                    <th>List</th><th>Discount</th><th>Net</th>
                    <th>Reserved</th><th>Redeemed</th><th>Released</th>
                  </tr>
                </thead>
                <tbody>
                  {redemptions.length === 0 && (
                    <tr><td colSpan={9} className="prm-empty">This code has never been applied.</td></tr>
                  )}
                  {redemptions.map(r => (
                    <tr key={r.id}>
                      <td>{r.org_name || r.org_id || '—'}</td>
                      <td><StateBadge state={r.state} /></td>
                      <td><code className="prm-code">{r.product_code || '—'}</code></td>
                      <td>{formatKrw(r.list_amount_krw)}</td>
                      <td>−{formatKrw(r.discount_krw)}</td>
                      <td><strong>{formatKrw(r.net_amount_krw)}</strong></td>
                      <td>{formatDateTime(r.reserved_at)}</td>
                      <td>{formatDateTime(r.redeemed_at)}</td>
                      <td>{formatDateTime(r.released_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="prm-modal-actions">
          <button className="prm-btn prm-btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Main screen ──────────────────────────────────────────────────────────────

export default function PromotionManager() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');   // what the admin is typing
  const [status, setStatus] = useState('');   // '' = every status
  const [q, setQ] = useState('');             // debounced, bound to the query param
  const [modal, setModal] = useState(null);   // { mode: 'create'|'detail'|'revoke', item }
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [revokeNotice, setRevokeNotice] = useState(null); // { code, still_reserved }

  const loadStats = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/admin/promotions/stats/summary');
      if (res.ok) setStats(await res.json());
    } catch { /* leave the em-dash placeholders in place */ }
  }, []);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page, per_page: PER_PAGE });
    if (q) params.append('q', q);
    if (status) params.append('status', status);
    try {
      const res = await apiFetch(`/api/v1/admin/promotions?${params}`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setItems(data.items || []);
        setTotal(data.total || 0);
        setError('');
      } else {
        setError(errorText(data, `Failed to load coupons (HTTP ${res.status})`));
      }
    } catch {
      setError('Network error loading coupons');
    }
  }, [page, q, status]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { load(); }, [load]);

  // Debounced search — same 250ms idiom as the user typeahead in
  // ManualPaymentManager.jsx, so a fast typist is not one request per keystroke.
  useEffect(() => {
    const next = search.trim();
    const handle = setTimeout(() => {
      setQ(next);
      setPage(1);   // a new query invalidates the offset
    }, 250);
    return () => clearTimeout(handle);
  }, [search]);

  const handleRevoke = async (coupon, reason) => {
    setError('');
    setMessage('');
    try {
      const res = await apiFetch(`/api/v1/admin/promotions/${coupon.id}/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setModal(null);
        if (data.status === 'already_revoked') {
          setMessage(`${coupon.code} was already revoked — nothing changed.`);
        } else {
          setMessage(`${data.code || coupon.code} revoked.`);
          // In-flight checkouts still settle at the discounted price — that
          // number is the point of the whole confirm step, so it gets its own
          // banner rather than a line in a toast that scrolls away.
          if (Number(data.still_reserved) > 0) {
            setRevokeNotice({ code: data.code || coupon.code, still_reserved: Number(data.still_reserved) });
          }
        }
        load();
        loadStats();
      } else {
        setError(errorText(data, 'Failed to revoke the coupon'));
      }
    } catch {
      setError('Network error while revoking the coupon');
    }
  };

  const breakdown = Array.isArray(stats?.promotion_breakdown) ? stats.promotion_breakdown : [];

  return (
    <div className="prm-page">
      <div className="prm-header">
        <h1>Promotion Manager</h1>
        <button className="prm-btn prm-btn-primary" onClick={() => setModal({ mode: 'create' })}>
          + Create Coupon
        </button>
      </div>

      {error && <div className="prm-error">{error}<button onClick={() => setError('')}>×</button></div>}
      {message && <div className="prm-msg">{message}<button onClick={() => setMessage('')}>×</button></div>}
      {revokeNotice && (
        <div className="prm-warn">
          <span>
            <strong>{revokeNotice.still_reserved} checkout
            {revokeNotice.still_reserved === 1 ? '' : 's'} still in flight</strong> on{' '}
            {revokeNotice.code}. Revocation is forward-only — those purchases were already
            created and will settle at the discounted price.
          </span>
          <button onClick={() => setRevokeNotice(null)}>×</button>
        </div>
      )}

      {/* Stats header */}
      <div className="prm-stats">
        <div className="prm-stat-card">
          <span className="prm-stat-value">{stats?.applied ?? '—'}</span>
          <span className="prm-stat-label">Applied</span>
        </div>
        <div className="prm-stat-card prm-stat-success">
          <span className="prm-stat-value">{stats?.converted ?? '—'}</span>
          <span className="prm-stat-label">Converted</span>
        </div>
        <div className="prm-stat-card prm-stat-accent">
          <span className="prm-stat-value">
            {stats ? `${stats.conversion_rate ?? 0}%` : '—'}
          </span>
          <span className="prm-stat-label">Conversion rate</span>
        </div>
        <div className="prm-stat-card">
          <span className="prm-stat-value">{stats?.orgs ?? '—'}</span>
          <span className="prm-stat-label">Organisations</span>
        </div>
        <div className="prm-stat-card prm-stat-warning">
          <span className="prm-stat-value">
            {stats ? formatKrw(stats.discount_given_krw) : '—'}
          </span>
          <span className="prm-stat-label">Discount given</span>
        </div>
        <div className="prm-stat-card prm-stat-success">
          <span className="prm-stat-value">
            {stats ? formatKrw(stats.net_revenue_krw) : '—'}
          </span>
          <span className="prm-stat-label">Net revenue</span>
        </div>
      </div>

      {/* Which promotion is working */}
      <div className="prm-section">
        <h2>Promotions</h2>
        <p className="prm-section-desc">
          Applied counts every checkout the code was attached to; converted counts the
          ones that were paid. The gap between them is abandonment, not demand.
        </p>
        <div className="prm-table-wrap">
          <table className="prm-table">
            <thead>
              <tr>
                <th>Promotion</th><th>Discount</th><th>Codes</th>
                <th>Applied</th><th>Converted</th><th className="prm-th-rate">Conversion rate</th>
                <th>Discount given</th><th>Net revenue</th><th>Last used</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.length === 0 && (
                <tr>
                  <td colSpan={9} className="prm-empty">
                    {stats ? 'No coupon has been applied yet.' : 'Loading…'}
                  </td>
                </tr>
              )}
              {breakdown.map(b => (
                <tr key={b.promotion}>
                  <td><code className="prm-code">{b.promotion}</code></td>
                  <td>{discountLabel(
                    b.discount_type,
                    b.discount_type === 'fixed' ? b.discount_value : null,
                    b.discount_type === 'percent' ? b.discount_value : null,
                  )}</td>
                  <td>{b.codes}</td>
                  <td>{b.applied}</td>
                  <td>{b.converted}</td>
                  <td className="prm-td-rate"><RateCell rate={b.conversion_rate} applied={b.applied} /></td>
                  <td>{formatKrw(b.discount_given_krw)}</td>
                  <td>{formatKrw(b.net_revenue_krw)}</td>
                  <td>{formatDate(b.last_used_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Coupons */}
      <div className="prm-section">
        <h2>Coupons</h2>
        <div className="prm-toolbar">
          <input
            type="text"
            placeholder="Search code or campaign..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {/* Filtered in SQL, inside a subquery that wraps the derived-status
              projection, so the count and the page agree. An earlier backend
              applied this AFTER pagination, which silently filtered within the
              current page while `total` stayed unfiltered — the control was
              deliberately left out until that was fixed. */}
          <select
            value={status}
            onChange={e => { setStatus(e.target.value); setPage(1); }}
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="revoked">Revoked</option>
            <option value="exhausted">Exhausted</option>
          </select>
        </div>
        <div className="prm-table-wrap">
          <table className="prm-table">
            <thead>
              <tr>
                <th>Code</th><th>Campaign</th><th>Discount</th><th>Scope</th>
                <th>Uses</th><th>Status</th><th>Expires</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={8} className="prm-empty">No coupons found</td></tr>
              )}
              {items.map(c => (
                <tr
                  key={c.id}
                  className="prm-row-click"
                  onClick={() => setModal({ mode: 'detail', item: c })}
                >
                  <td><code className="prm-code">{c.code}</code></td>
                  <td>{c.campaign_code || '—'}</td>
                  <td>{discountLabel(c.discount_type, c.amount_off_krw, c.percent_off)}</td>
                  <td>{c.product_code || <span className="prm-muted">Any plan</span>}</td>
                  <td>
                    <div className="prm-uses">
                      <span>{c.live_redemptions ?? 0} / {c.max_redemptions ?? '∞'}</span>
                      <small>{c.converted ?? 0} converted</small>
                    </div>
                  </td>
                  <td><StatusBadge status={c.status} /></td>
                  <td>{formatDate(c.expires_at)}</td>
                  <td onClick={e => e.stopPropagation()}>
                    <button
                      className="prm-btn-link"
                      onClick={() => setModal({ mode: 'detail', item: c })}
                    >
                      View
                    </button>
                    {c.status !== 'revoked' && (
                      <button
                        className="prm-btn-link prm-btn-link-danger"
                        onClick={() => setModal({ mode: 'revoke', item: c })}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="prm-pagination">
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</button>
          <span>Page {page} — {total} total</span>
          <button disabled={page * PER_PAGE >= total} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      </div>

      {modal?.mode === 'create' && (
        <CreateModal
          onClose={() => setModal(null)}
          onCreated={() => { load(); loadStats(); }}
        />
      )}
      {modal?.mode === 'detail' && (
        <DetailModal couponId={modal.item.id} onClose={() => setModal(null)} />
      )}
      {modal?.mode === 'revoke' && (
        <RevokeModal
          coupon={modal.item}
          onClose={() => setModal(null)}
          onConfirm={handleRevoke}
        />
      )}
    </div>
  );
}
